/**
 * WhatsApp Cloud API Template type definitions
 * Based on Meta Business Management API documentation
 */

export const WHATSAPP_TEMPLATE_TYPES = [
  {
    value: 'TEXT',
    labelKey: 'WHATSAPP_TEMPLATES.CREATE.TYPES.TEXT',
    descriptionKey: 'WHATSAPP_TEMPLATES.CREATE.TYPES.TEXT_DESCRIPTION',
    icon: 'i-lucide-type',
    enabled: true,
  },
  {
    value: 'MEDIA',
    labelKey: 'WHATSAPP_TEMPLATES.CREATE.TYPES.MEDIA',
    descriptionKey: 'WHATSAPP_TEMPLATES.CREATE.TYPES.MEDIA_DESCRIPTION',
    icon: 'i-lucide-image',
    enabled: true,
  },
  {
    value: 'QUICK_REPLY',
    labelKey: 'WHATSAPP_TEMPLATES.CREATE.TYPES.QUICK_REPLY',
    descriptionKey: 'WHATSAPP_TEMPLATES.CREATE.TYPES.QUICK_REPLY_DESCRIPTION',
    icon: 'i-lucide-reply',
    enabled: true,
  },
  {
    value: 'CALL_TO_ACTION',
    labelKey: 'WHATSAPP_TEMPLATES.CREATE.TYPES.CALL_TO_ACTION',
    descriptionKey:
      'WHATSAPP_TEMPLATES.CREATE.TYPES.CALL_TO_ACTION_DESCRIPTION',
    icon: 'i-lucide-phone',
    enabled: true,
  },
  {
    value: 'AUTHENTICATION',
    labelKey: 'WHATSAPP_TEMPLATES.CREATE.TYPES.AUTHENTICATION',
    descriptionKey:
      'WHATSAPP_TEMPLATES.CREATE.TYPES.AUTHENTICATION_DESCRIPTION',
    icon: 'i-lucide-shield-check',
    enabled: false,
  },
];

/**
 * Template categories supported by Meta WhatsApp Cloud API
 */
export const WHATSAPP_TEMPLATE_CATEGORIES = [
  {
    value: 'UTILITY',
    labelKey: 'WHATSAPP_TEMPLATES.CREATE.CATEGORIES.UTILITY',
    descriptionKey: 'WHATSAPP_TEMPLATES.CREATE.CATEGORIES.UTILITY_DESCRIPTION',
  },
  {
    value: 'MARKETING',
    labelKey: 'WHATSAPP_TEMPLATES.CREATE.CATEGORIES.MARKETING',
    descriptionKey:
      'WHATSAPP_TEMPLATES.CREATE.CATEGORIES.MARKETING_DESCRIPTION',
  },
  {
    value: 'AUTHENTICATION',
    labelKey: 'WHATSAPP_TEMPLATES.CREATE.CATEGORIES.AUTHENTICATION',
    descriptionKey:
      'WHATSAPP_TEMPLATES.CREATE.CATEGORIES.AUTHENTICATION_DESCRIPTION',
  },
];

/**
 * Language options supported by Meta WhatsApp Cloud API
 */
export const WHATSAPP_LANGUAGE_OPTIONS = [
  { value: 'pt_BR', label: 'Português (Brasil)' },
  { value: 'en', label: 'English' },
];

/**
 * Button types for WhatsApp templates
 */
export const WHATSAPP_BUTTON_TYPES = {
  QUICK_REPLY: 'QUICK_REPLY',
  URL: 'URL',
  PHONE_NUMBER: 'PHONE_NUMBER',
  COPY_CODE: 'COPY_CODE',
  VOICE_CALL: 'VOICE_CALL',
  VOICE_CALL_REQUEST: 'VOICE_CALL_REQUEST',
};

/**
 * Icon mapping for button types
 */
export const WHATSAPP_BUTTON_TYPE_ICONS = {
  URL: 'i-lucide-link',
  PHONE_NUMBER: 'i-lucide-phone',
  COPY_CODE: 'i-lucide-copy',
  VOICE_CALL: 'i-lucide-phone-call',
  VOICE_CALL_REQUEST: 'i-lucide-phone-incoming',
};

/**
 * Media formats for header
 */
export const WHATSAPP_MEDIA_FORMATS = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  DOCUMENT: 'DOCUMENT',
};

/**
 * MIME types accepted for a media header
 */
export const WHATSAPP_MEDIA_ACCEPT =
  'image/jpeg,image/png,video/mp4,video/3gpp,application/pdf';

/**
 * OTP types for authentication templates
 */
export const WHATSAPP_OTP_TYPES = {
  COPY_CODE: 'COPY_CODE',
  ONE_TAP: 'ONE_TAP',
  ZERO_TAP: 'ZERO_TAP',
};

/**
 * Template limits based on Meta documentation
 */
export const WHATSAPP_TEMPLATE_LIMITS = {
  NAME_MAX_LENGTH: 512,
  BODY_MAX_LENGTH: 1024,
  FOOTER_MAX_LENGTH: 60,
  BUTTON_TEXT_MAX_LENGTH: 25,
  MAX_BUTTONS: 10,
  MAX_URL_BUTTONS: 2,
  MAX_PHONE_BUTTONS: 1,
  MAX_COPY_CODE_BUTTONS: 1,
  MAX_VOICE_CALL_BUTTONS: 1,
  MAX_VOICE_CALL_REQUEST_BUTTONS: 1,
  MAX_QUICK_REPLY_BUTTONS: 10,
  COPY_CODE_MAX_LENGTH: 15,
  CODE_EXPIRATION_MIN: 1,
  CODE_EXPIRATION_MAX: 90,
  MIN_TTL_MINUTES: 1,
  MAX_TTL_MINUTES: 43200, // 30 days
  DEFAULT_TTL_MINUTES: 10080, // 7 days
};

/**
 * Regex patterns for validation
 */
export const WHATSAPP_VARIABLE_PATTERN = /\{\{(\d+)\}\}/g;
export const WHATSAPP_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;

/**
 * File extension patterns for WhatsApp media auto-detection
 * Based on Meta WhatsApp Cloud API supported formats
 */
export const WHATSAPP_MEDIA_FILE_EXTENSIONS = {
  IMAGE: /\.(jpg|jpeg|png)([?#].*)?$/i,
  VIDEO: /\.(mp4|3gp|3gpp)([?#].*)?$/i,
  DOCUMENT: /\.(pdf)([?#].*)?$/i,
};

/**
 * Detect media type from URL file extension
 * @param {string} url - Media URL
 * @returns {string|null} - 'IMAGE', 'VIDEO', 'DOCUMENT', or null if unknown
 */
export const detectWhatsappMediaType = url => {
  if (!url) return null;

  if (WHATSAPP_MEDIA_FILE_EXTENSIONS.IMAGE.test(url)) {
    return 'IMAGE';
  }

  if (WHATSAPP_MEDIA_FILE_EXTENSIONS.VIDEO.test(url)) {
    return 'VIDEO';
  }

  if (WHATSAPP_MEDIA_FILE_EXTENSIONS.DOCUMENT.test(url)) {
    return 'DOCUMENT';
  }

  return null;
};
